import type { Input } from "../../../../api/call";
import type { Result } from "../../../../lib/patterns/result";
import { Err, Ok, isResultErr } from "../../../../lib/patterns/result";
import type { FILE_PARSING_ERROR } from "../../common/file";
import { parseFile } from "../../common/file";
import type { Parser, ParsingError, ParsingErrorMessageMap, ParsingResult } from "../../types";
import {
  columnsMandatory,
  hasDuplicatedRowByQuantityId,
  hasInvalidColumns,
  hasInvalidDefinitionOffsetExpected,
  hasInvalidDefinitionOutputModelName,
  hasInvalidFileEmpty,
  hasInvalidOffsetExpected,
  hasInvalidOutputModelNameSize,
  hasNoValueForQuantityName,
  hasUnexpectedQuantityName,
} from "./rules";

export type OutputModelParsingResultData = Input<
  "POST",
  "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/outputs"
>["body"]["outputs"];

export type OutputModelParsingResult = ParsingResult<OutputModelParsingResultData>;

/**
 * @description Error that can be thrown by the output model parser.
 */
export enum OUTPUT_MODEL_PARSING_ERROR {
  DATA_FILE_EMPTY = "DATA_FILE_EMPTY",
  FILE_INVALID_CONTENT = "FILE_INVALID_CONTENT",
  DATA_QUANTITY_DUPLICATED = "DATA_QUANTITY_DUPLICATED",
  DATA_QUANTITY_NAME_EMPTY = "DATA_QUANTITY_NAME_EMPTY",
  DATA_QUANTITY_NAME_NOT_ALLOWED = "DATA_QUANTITY_NAME_NOT_ALLOWED",
  DATA_OFFSET_EXPECTED_INVALID = "DATA_OFFSET_EXPECTED_INVALID",
  DATA_OUTPUT_MODEL_INVALID_DEFINITION = "DATA_OUTPUT_MODEL_INVALID_DEFINITION",
  DATA_OFFSET_EXPECTED_INVALID_DEFINITION = "DATA_OFFSET_EXPECTED_INVALID_DEFINITION",
  DATA_OUTPUT_MODEL_NAME_TOO_LONG = "DATA_OUTPUT_MODEL_NAME_TOO_LONG",
}

export type OutputModelParsingErrorMessageMap = ParsingErrorMessageMap<
  OUTPUT_MODEL_PARSING_ERROR | FILE_PARSING_ERROR
>;

export type OutputModelParsingError = ParsingError<OUTPUT_MODEL_PARSING_ERROR | FILE_PARSING_ERROR>;

/**
 * @description Parse input model file content.
 * @param file Input model file to parse
 * @returns
 */
export const parse: Parser<
  OutputModelParsingResult,
  OutputModelParsingError[],
  { expectedQuantityNameValues: string[]; quantityIdByName: Record<string, string> }
> = async (file, { expectedQuantityNameValues, quantityIdByName }) => {
  /**
   * @description Check for common file issue.
   */
  const errors: OutputModelParsingError[] = [];
  const fileResult = await parseFile(file, {
    expectedMimeType: ["text/csv", "application/vnd.ms-excel"],
  });

  /** Have to stop here, to handle file error */
  if (isResultErr(fileResult)) {
    errors.push(fileResult.value);
    return Err(errors);
  }

  /**
   * Get file content.
   */
  const content = fileResult.value as string;

  /**
   * Formatted file content.
   */
  const [header, ...data] = content.split("\n").map(line =>
    line
      .replace(/\r/, "")
      .split(";")
      .map(v => v.replaceAll('"', "")),
  );

  /**
   * Format the data to be processed
   */
  const indiceByColumnNameData = columnsMandatory.reduce<{ [key: string]: number }>((obj, cols) => {
    const indexColumnData = header ? header.findIndex(col => cols.includes(col)) : -1;
    return {
      ...obj,
      ...cols.reduce((acc, columnName) => ({ ...acc, [columnName]: indexColumnData }), {}),
    };
  }, {});

  const columnHeaderNormalizes = columnsMandatory.flatMap(([col]) => col ?? []);
  const targetData = data.map(line =>
    columnHeaderNormalizes.reduce<{ [key: string]: string | undefined }>(
      (acc, columnName) => ({
        ...acc,
        [columnName]: line[indiceByColumnNameData[columnName] ?? -1],
      }),
      {},
    ),
  );

  /**
   * Check Format
   */
  if (hasInvalidColumns(header))
    errors.push({
      kind: OUTPUT_MODEL_PARSING_ERROR.FILE_INVALID_CONTENT,
      explain: "file don't contains expected columns",
      location: { file: file.name, line: 0 },
      data: {
        hint: columnsMandatory.join(", "),
        value: header?.join(", "),
      },
    });

  /**
   * Filters model output data by excluding model output data.
   */
  const targetDataForOutputModel = targetData.filter(
    line =>
      (line["outputModelName"] && line["outputModelName"].trim().length > 0) ||
      line["offsetExpected"],
  );

  /**
   * Check global data.
   */
  if (hasInvalidFileEmpty(targetDataForOutputModel))
    errors.push({
      kind: OUTPUT_MODEL_PARSING_ERROR.DATA_FILE_EMPTY,
      explain: "empty data file",
      location: { file: file.name, line: 0 },
      data: {
        value: data.length,
      },
    });

  const quantityIds = targetDataForOutputModel
    .map(({ name: quantityId } = {}) => quantityId)
    .filter(Boolean);
  if (hasDuplicatedRowByQuantityId(quantityIds))
    errors.push({
      kind: OUTPUT_MODEL_PARSING_ERROR.DATA_QUANTITY_DUPLICATED,
      explain: "file contain duplicated row",
      location: { file: file.name, line: 0 },
      data: {
        key: "name",
        value: quantityIds.find((quantityId, index) => quantityIds.indexOf(quantityId) !== index),
      },
    });

  /**
   * @description Check data.
   * @see {parseData}
   */
  const dataResult = parseData(file, targetDataForOutputModel, expectedQuantityNameValues);
  if (isResultErr(dataResult)) errors.push(...dataResult.value);

  /** if any errors, return them, otherwise return parser result */
  return errors.length > 0
    ? Err(errors)
    : Ok({
        version: header?.[0] === "name" ? "lithium" : "helium",
        data: (dataResult.value as OutputModelParsingResultData).map(v => ({
          ...v,
          quantityId: quantityIdByName[v.quantityId] as string,
        })),
      });
};

interface OutputModel {
  quantityId: string;
  modelName: string;
  offsetExpected: number;
}

const parseData = (
  file: File,
  data: {
    [key: string]: string | undefined;
  }[],
  expectedNameValues: string[],
): Result<OutputModelParsingResultData, OutputModelParsingError[]> => {
  const errors: OutputModelParsingError[] = [];
  const outputModels: OutputModel[] = [];

  /**
   * Check by line data.
   */
  for (let line = 0; line < data.length; line++) {
    const {
      name: quantityId = "",
      outputModelName: modelName = "",
      offsetExpected = "",
    } = data[line] || {};

    if (hasInvalidOutputModelNameSize(modelName))
      errors.push({
        kind: OUTPUT_MODEL_PARSING_ERROR.DATA_OUTPUT_MODEL_NAME_TOO_LONG,
        explain: "'outputModelName' is too long",
        location: { file: file.name, line },
        data: {
          key: "outputModelName",
          value: quantityId,
          hint: modelName,
        },
      });

    if (hasNoValueForQuantityName(quantityId))
      errors.push({
        kind: OUTPUT_MODEL_PARSING_ERROR.DATA_QUANTITY_NAME_EMPTY,
        explain: "file missing value for column 'name' or 'technicalName'",
        location: { file: file.name, line },
        data: {
          key: "name",
          value: quantityId,
          hint: expectedNameValues.join(", "),
        },
      });

    if (hasUnexpectedQuantityName(quantityId, expectedNameValues))
      errors.push({
        kind: OUTPUT_MODEL_PARSING_ERROR.DATA_QUANTITY_NAME_NOT_ALLOWED,
        explain: "file contains unexpected value for column 'name' or 'technicalName'",
        location: { file: file.name, line },
        data: {
          key: "name",
          hint: expectedNameValues.join(", "),
          value: quantityId,
        },
      });

    if (hasInvalidOffsetExpected(offsetExpected))
      errors.push({
        kind: OUTPUT_MODEL_PARSING_ERROR.DATA_OFFSET_EXPECTED_INVALID,
        explain: "file contains unexpected value for column 'offsetExpected'",
        location: { file: file.name, line },
        data: {
          key: "offsetExpected",
          hint: offsetExpected,
          value: quantityId,
        },
      });

    if (hasInvalidDefinitionOutputModelName(modelName, offsetExpected))
      errors.push({
        kind: OUTPUT_MODEL_PARSING_ERROR.DATA_OUTPUT_MODEL_INVALID_DEFINITION,
        explain: "file contains unexpected value for column 'outputModelName'",
        location: { file: file.name, line },
        data: {
          key: "offsetExpected",
          hint: offsetExpected,
          value: quantityId,
        },
      });

    if (hasInvalidDefinitionOffsetExpected(modelName, offsetExpected))
      errors.push({
        kind: OUTPUT_MODEL_PARSING_ERROR.DATA_OFFSET_EXPECTED_INVALID_DEFINITION,
        explain: "file contains unexpected value for column 'offsetExpected'",
        location: { file: file.name, line },
        data: {
          key: "outputModelName",
          hint: modelName,
          value: quantityId,
        },
      });

    /**
     * Everything is fine, we can add the input model to the list.
     */
    outputModels.push({
      quantityId,
      modelName,
      offsetExpected: Number(offsetExpected),
    });
  }

  return errors.length > 0 ? Err(errors) : Ok(outputModels);
};
